import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { CandleCanvas } from "./CandleCanvas";
import { EASE } from "./Reveal";

const IG_DM = "https://ig.me/m/bctrades__";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const canvasX = useTransform(sx, (v) => v * 30);
  const canvasY = useTransform(sy, (v) => v * 20);
  const glowX = useTransform(sx, (v) => v * -70);
  const glowY = useTransform(sy, (v) => v * -50);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const goTo = (sel) => {
    if (window.__lenis) window.__lenis.scrollTo(sel, { duration: 1.6 });
    else document.querySelector(sel)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      className="relative flex min-h-screen flex-col overflow-hidden"
    >
      <motion.div style={{ x: canvasX, y: canvasY }} className="absolute -inset-10 opacity-70">
        <CandleCanvas className="h-full w-full" />
      </motion.div>
      <motion.div
        style={{ x: glowX, y: glowY }}
        className="pointer-events-none absolute left-1/2 top-1/3 h-[60vmin] w-[60vmin] -translate-x-1/2 rounded-full bg-[#2F7DF6] opacity-[0.14] blur-[120px]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#020A1A]/70 via-transparent to-[#020A1A]" />

      <motion.header
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
        className="relative z-10 flex items-center justify-between px-6 pt-6 font-mono text-[11px] tracking-[0.3em] text-[#7A90B8] sm:px-12 sm:text-xs lg:px-20"
      >
        <span data-testid="hero-brand">BC TRADES<span className="text-[#2F7DF6]">®</span></span>
        <span className="hidden sm:block">FORMACIÓN DE TRADING — POL &amp; EDGAR</span>
        <a
          data-testid="nav-instagram-link"
          href="https://instagram.com/bctrades__"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-1.5 text-[#DCE8FF] transition-colors hover:text-[#5EA2FF]"
        >
          @BCTRADES__
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </motion.header>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex flex-1 flex-col justify-center px-6 py-16 sm:px-12 lg:px-20"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 font-mono text-[11px] tracking-[0.4em] text-[#5EA2FF] sm:text-xs"
          data-testid="hero-eyebrow"
        >
          {"// PRECIO · LIQUIDEZ · ESTRUCTURA · MANIPULACIÓN · CONTEXTO"}
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.82] tracking-tight">
          <MaskedLine delay={0.25} className="text-[clamp(2.8rem,12vw,11rem)] text-white sm:text-[clamp(4.5rem,15vw,12rem)]">
            BC
          </MaskedLine>
          <MaskedLine delay={0.4} className="text-stroke-blue text-[clamp(2.8rem,12vw,11rem)] sm:text-[clamp(4.5rem,15vw,12rem)]">
            TRADES
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="max-w-md text-sm leading-relaxed text-[#7A90B8] sm:text-base"
            data-testid="hero-tagline"
          >
            No te enseñamos estrategias con nombres complejos. Te enseñamos a entender
            cómo se mueve <span className="text-white">realmente</span> el mercado, para
            operar con lógica, disciplina y consistencia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-pricing-button"
              onClick={() => goTo("#productos")}
              className="group flex items-center gap-3 bg-[#2F7DF6] px-7 py-4 font-mono text-xs font-bold tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#5EA2FF]"
            >
              VER ACCESO
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-cta-instagram-button"
              href={IG_DM}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-[#5EA2FF]/40 px-7 py-4 font-mono text-xs tracking-[0.25em] text-[#DCE8FF] transition-colors duration-300 hover:border-[#5EA2FF] hover:text-[#5EA2FF]"
            >
              HABLAR POR INSTAGRAM
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 flex items-center justify-between border-t border-[#5EA2FF]/15 px-6 py-4 font-mono text-[10px] tracking-[0.3em] text-[#7A90B8] sm:px-12 sm:text-[11px] lg:px-20"
        data-testid="hero-stats-bar"
      >
        <span>28 CLASES</span>
        <span className="hidden sm:inline">+30 VÍDEOS</span>
        <span className="hidden md:inline">+700 DIAPOSITIVAS</span>
        <span className="flex items-center gap-2 text-[#5EA2FF]">
          SCROLL <ArrowDown className="h-3 w-3 animate-bounce" />
        </span>
      </motion.div>
    </section>
  );
};
